/**
 * Quién está dentro, en la cabecera, y qué puede hacer al respecto (§2.a).
 *
 * Sustituye al indicador de llave y proveedor, que era diagnóstico interno
 * y no le decía nada a quien usa la herramienta. En su lugar va la empresa
 * —que es como la gente identifica su espacio de trabajo— y un avatar.
 */

import { Building2, LogOut, Wallet } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSalir } from '../api/sesion'
import type { Usuario } from '../api/tipos'
import { cx, formatearCosto } from '../ui/util'

/** Iniciales para el avatar: del nombre, o del correo si no hay nombre. */
function iniciales(usuario: Usuario): string {
  const partes = [usuario.nombre, usuario.apellido].filter(Boolean)
  if (partes.length) return partes.map((p) => p[0]!.toUpperCase()).join('')
  return (usuario.email[0] ?? '?').toUpperCase()
}

export function MenuUsuario({ usuario }: { usuario: Usuario }) {
  const navegar = useNavigate()
  const salir = useSalir()
  const [abierto, setAbierto] = useState(false)
  const contenedor = useRef<HTMLDivElement>(null)

  // Cerrar al pinchar fuera o con Escape: sin esto el menú se queda colgado
  // y tapa la cabecera.
  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false)
    }
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false)
    }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', escape)
    }
  }, [abierto])

  const perfil = usuario.perfil
  const disponibles = Number(perfil?.creditos_disponibles ?? 0)

  return (
    <div ref={contenedor} className="relative">
      <button
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-[var(--color-acento-suave)]"
      >
        <span className="hidden max-w-40 truncate text-[12px] text-[var(--color-texto-suave)] sm:block">
          {perfil?.empresa || usuario.nombre_completo}
        </span>
        <span
          aria-hidden
          className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--color-acento)] text-[11px] font-semibold text-white"
        >
          {iniciales(usuario)}
        </span>
      </button>

      {abierto && (
        <div
          role="menu"
          className="aparecer absolute right-0 z-40 mt-1 w-64 overflow-hidden rounded-md border border-[var(--color-borde-fuerte)] bg-[var(--color-panel)] shadow-lg"
        >
          <div className="border-b border-[var(--color-borde)] px-3 py-2.5">
            <p className="truncate text-[13px] font-medium">{usuario.nombre_completo}</p>
            <p className="truncate text-[12px] text-[var(--color-texto-suave)]">
              {usuario.email}
            </p>
            {perfil?.empresa && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-[var(--color-texto-suave)]">
                <Building2 size={12} className="shrink-0" />
                <span className="truncate">{perfil.empresa}</span>
              </p>
            )}
          </div>

          {perfil && (
            <div className="border-b border-[var(--color-borde)] px-3 py-2.5 text-[12px]">
              <p className="flex items-center gap-1.5 text-[var(--color-texto-suave)]">
                <Wallet size={12} className="shrink-0" />
                Créditos
              </p>
              <p
                className={cx(
                  'mt-1 tabular',
                  disponibles <= 0 && 'text-[var(--color-opositora)]',
                )}
              >
                <span className="text-[14px] font-semibold">{formatearCosto(disponibles)}</span>{' '}
                <span className="text-[var(--color-texto-tenue)]">
                  de {formatearCosto(Number(perfil.creditos_asignados))}
                </span>
              </p>
              <p className="mt-0.5 text-[var(--color-texto-tenue)]">
                {perfil.n_investigaciones}{' '}
                {perfil.n_investigaciones === 1 ? 'investigación' : 'investigaciones'}
              </p>
            </div>
          )}

          <button
            role="menuitem"
            onClick={() =>
              salir.mutate(undefined, {
                // Al salir se vuelve al login (§2.a). `replace` evita que el
                // botón de atrás devuelva a una vista ya sin sesión.
                onSettled: () => navegar('/login', { replace: true }),
              })
            }
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] hover:bg-[var(--color-acento-suave)]"
          >
            <LogOut size={14} className="shrink-0" />
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  )
}
